// Message Service for handling all message-related API operations
import { apiEndpoints } from '../config/env'
import { apiService } from './apiService'

export interface Message {
  messageID: number
  messageText: string
  creationDate: string
  assignedToID_FK: number
  assignedToName: string
  completed: boolean
  hasBeenUpdated: boolean
}

export interface MessageCount {
  unreadCount: number
  readButNotCompletedCount: number
  totalCount: number
}

export interface CreateMessage {
  messageText: string
  assignedToID_FK: number
  creationDate?: string
}

export interface UpdateMessage {
  messageText?: string
  completed?: boolean
  hasBeenUpdated?: boolean
}

class MessageService {
  // Get message count for a technician on a specific date
  async getMessageCount(technicianId: number, date?: string): Promise<MessageCount> {
    try {
      const params = new URLSearchParams({
        technicianId: technicianId.toString(),
        ...(date && { date })
      })

      return await apiService.get<MessageCount>(`${apiEndpoints.messages.count}?${params}`)
    } catch (error) {
      console.error('Error fetching message count:', error)
      throw error
    }
  }

  // Get messages for a technician on a specific date
  async getMessages(technicianId: number, date?: string): Promise<Message[]> {
    try {
      const params = new URLSearchParams({
        technicianId: technicianId.toString(),
        ...(date && { date })
      })

      return await apiService.get<Message[]>(`${apiEndpoints.messages.base}?${params}`)
    } catch (error) {
      console.error('Error fetching messages:', error)
      throw error
    }
  }

  // Get a specific message by ID
  async getMessage(messageId: number): Promise<Message> {
    try {
      return await apiService.get<Message>(apiEndpoints.messages.get(messageId))
    } catch (error) {
      console.error('Error fetching message:', error)
      throw error
    }
  }

  // Create a new message
  async createMessage(message: CreateMessage): Promise<Message> {
    try {
      return await apiService.post<Message>(apiEndpoints.messages.base, message)
    } catch (error) {
      console.error('Error creating message:', error)
      throw error
    }
  }

  // Update an existing message
  async updateMessage(messageId: number, update: UpdateMessage): Promise<Message> {
    try {
      return await apiService.put<Message>(apiEndpoints.messages.get(messageId), update)
    } catch (error) {
      console.error('Error updating message:', error)
      throw error
    }
  }

  // Delete a message
  async deleteMessage(messageId: number): Promise<void> {
    try {
      await apiService.delete(apiEndpoints.messages.get(messageId))
    } catch (error) {
      console.error('Error deleting message:', error)
      throw error
    }
  }

  // Mark a message as read
  async markAsRead(messageId: number): Promise<void> {
    try {
      await apiService.patch(apiEndpoints.messages.markAsRead(messageId), {})
    } catch (error) {
      console.error('Error marking message as read:', error)
      throw error
    }
  }

  // Mark a message as completed or not completed
  async markAsCompleted(messageId: number, completed: boolean = true): Promise<void> {
    try {
      await apiService.patch(apiEndpoints.messages.markAsCompleted(messageId, completed), {})
    } catch (error) {
      console.error('Error marking message as completed:', error)
      throw error
    }
  }

  // Helper method to determine message status for UI
  getMessageStatus(message: Message): 'unread' | 'read' | 'completed' {
    if (message.completed) {
      return 'completed'
    }
    if (message.hasBeenUpdated) {
      return 'read'
    }
    return 'unread'
  }

  // Helper method to get the button styling class based on message count
  getButtonStyle(messageCount: MessageCount): 'btn-danger' | 'btn-warning' | 'btn-secondary' {
    if (messageCount.unreadCount > 0) {
      return 'btn-danger'
    }
    if (messageCount.readButNotCompletedCount > 0) {
      return 'btn-warning'
    }
    return 'btn-secondary'
  }
}

export const messageService = new MessageService()